import { ListenerSet } from "./ListenerSet.js";
function assertFunction(value, key) {
    if (typeof value !== "function") {
        throw new Error(`Expected function for key "${key}", got ${typeof value}`);
    }
}
/**
 * A map of named listener sets. Each key maps to a `ListenerSet` that can have
 * multiple listeners registered. Typed through `T` so that `register` and
 * `compose` preserve per-key callback signatures.
 *
 * All keys are pre-initialized in the constructor so `register` can validate
 * keys. `compose()` only includes keys with at least one registered listener,
 * preserving callback-presence semantics used by the client as feature guards.
 * For included keys, composed functions delegate to the live listener set, so
 * listeners added/removed after `compose()` still take effect. Keys with no
 * listeners at compose time are omitted entirely; call `compose()` again after
 * registering listeners to pick up newly populated keys.
 */
export class ListenerMap {
    sets = new Map();
    constructor(keys) {
        for (const key of keys) {
            this.sets.set(key, new ListenerSet());
        }
    }
    /**
     * Register listeners for one or more keys. Returns a function that removes
     * all listeners added by this call.
     */
    register(callbacks) {
        const removers = Object.entries(callbacks)
            .filter(([, fn]) => fn !== undefined)
            .map(([key, fn]) => {
            assertFunction(fn, key);
            const set = this.sets.get(key);
            if (!set) {
                throw new Error(`Unknown callback key "${key}"`);
            }
            return set.add(fn);
        });
        return () => {
            for (const remove of removers)
                remove();
        };
    }
    /**
     * Compose all registered listeners into a single callbacks object. Each
     * composed function delegates to the live listener set, so listeners
     * added/removed after this call still take effect.
     */
    compose() {
        return Object.fromEntries(Array.from(this.sets.entries())
            .filter(([, set]) => set.size > 0)
            .map(([key, set]) => [
            key,
            (...args) => {
                set.invoke(...args);
            },
        ]));
    }
}
//# sourceMappingURL=ListenerMap.js.map